import { GRADING_THRESHOLDS } from '../../shared/constants.js';

/**
 * Counts open grading uncertainties (low/medium confidence, unapproved) across submissions for a
 * set of main task numbers. Shared by the grading tool and the reupload regrade announcements so
 * both surface the same "should we open the correction canvas?" signal.
 */
type ConfidenceLevel = 'none' | 'low' | 'medium';

const unwrapDoc = <T>(value: T | { doc?: T } | null | undefined): T | undefined => {
  if (!value) return undefined;
  if (typeof value === 'object' && value !== null && 'doc' in (value as any)) {
    return ((value as any).doc ?? undefined) as T | undefined;
  }
  return value as T;
};

const resolveConfidenceLevelSignal = (gradingResult: any, boxes: any[] | undefined): ConfidenceLevel => {
  if (gradingResult?.isApproved === true) return 'none';

  const candidates: number[] = [];

  const gradingConfidence = Number(gradingResult?.confidence);
  if (Number.isFinite(gradingConfidence)) candidates.push(gradingConfidence);

  if (Array.isArray(boxes)) {
    for (const box of boxes) {
      if (box?.approved === true) continue;
      const boxConfidence = Number(box?.confidence);
      if (Number.isFinite(boxConfidence)) candidates.push(boxConfidence);
    }
  }

  if (candidates.length === 0) return 'none';
  const minConfidence = Math.min(...candidates);
  if (minConfidence < GRADING_THRESHOLDS.LOW_CONFIDENCE) return 'low';
  if (minConfidence < GRADING_THRESHOLDS.MEDIUM_CONFIDENCE) return 'medium';
  return 'none';
};

export const countCorrectionModeUncertainties = (submissions: any[], mainTaskNumbers: string[]) => {
  const targetMainTasks = new Set(mainTaskNumbers.map(value => String(value)));
  let low = 0;
  let medium = 0;

  const walkTaskNode = (taskNode: any) => {
    const resolvedTask = unwrapDoc<any>(taskNode) ?? taskNode;
    const gradingResult = unwrapDoc<any>(resolvedTask?.gradingResult);
    const level = resolveConfidenceLevelSignal(gradingResult, resolvedTask?.boundingBoxes);

    if (level === 'low') low += 1;
    if (level === 'medium') medium += 1;

    const children = Array.isArray(resolvedTask?.subTasksSubmission) ? resolvedTask.subTasksSubmission : [];
    for (const child of children) {
      walkTaskNode(child);
    }
  };

  for (const submission of submissions) {
    const submissionTasks = Array.isArray(submission?.taskSubmissions) ? submission.taskSubmissions : [];
    for (const mainTask of submissionTasks) {
      const mainTaskNumber = String(mainTask?.taskNumber ?? '');
      if (!targetMainTasks.has(mainTaskNumber)) continue;
      walkTaskNode(mainTask);
    }
  }

  return {
    low,
    medium,
    total: low + medium,
  };
};
